// Ideation → agent queue. The Diagnose button's whole job.
//
// One ai_jobs row per client per click, not one per finding: diagnosing ten queries on one site
// shares the repo, the page tree and the internal-link picture, and splitting them would put ten
// jobs on the same repo in violation of one-build-per-repo the moment any of them got accepted.
//
// The diagnosis lands as the job's SPEC. Accepting it on the Ideation screen is approve-spec, and
// the fix becomes a build job like everything else on the board.

import { supabase } from './supabase';
import { enqueueAiJob, type AiJob } from './aiJobs';
import { perMonth, type SeoOpportunity } from './seoOpportunities';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt } from './prompts';
import type { IdeationBrief } from './supabase.types';

export type { IdeationBrief };

export type EnqueueIdeationInput = {
  clientId: string;
  clientName: string;
  /** Every row must belong to clientId. Group before calling. */
  opportunities: SeoOpportunity[];
  createdBy: { id: string; name: string };
  /** One of AI_JOB_MODELS ids. Omit / null = agent default. */
  model?: string | null;
};

function noonTodayIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString();
}

const titleFor = (clientName: string, count: number) =>
  `SEO diagnosis — ${clientName} (${count} finding${count === 1 ? '' : 's'})`;

/**
 * What the agent reads when it claims the job.
 *
 * Deliberately includes the baseline numbers rather than only the ids: the agent should never have
 * to re-derive what Search Console already said, and the measurements in the spec need to match
 * the ones the client is shown later.
 */
export function formatIdeationQueueNote(brief: IdeationBrief, template: string = PROMPT_DEFAULTS['seo-diagnosis']): string {
  const findings = brief.findings.map((f) => {
    const gain = perMonth(f.est_click_gain);
    return (
      `- [${f.kind}] "${f.query}"` +
      (f.url ? ` → ${f.url}` : ' → no URL on the finding') +
      (f.position == null ? '' : ` · pos ${f.position.toFixed(1)}`) +
      (f.impressions == null ? '' : ` · ${f.impressions.toLocaleString()} impr`) +
      (gain == null ? '' : ` · +${gain} clicks/mo if fixed`)
    );
  });
  return renderPrompt(template, { client: brief.client_name, findings: findings.join('\n'), opportunity_ids: brief.opportunity_ids.join(', ') });
}

export function buildIdeationBrief(input: EnqueueIdeationInput): IdeationBrief {
  return {
    client_id: input.clientId,
    client_name: input.clientName,
    opportunity_ids: input.opportunities.map((o) => o.id),
    findings: input.opportunities.map((o) => ({
      id: o.id,
      kind: o.kind,
      query: o.query,
      url: o.url,
      position: o.baseline?.position ?? null,
      impressions: o.baseline?.impressions ?? null,
      est_click_gain: o.est_click_gain,
    })),
  };
}

/**
 * Queue one diagnosis job and mark its rows as being read.
 *
 * The status stamp is the point of the whole rewrite: before it, the gap between clicking and an
 * answer had no state, so a run that died looked exactly like a row nobody had touched.
 */
export async function enqueueIdeationJob(input: EnqueueIdeationInput): Promise<AiJob> {
  if (!input.clientId) throw new Error('An opportunity with no client cannot be diagnosed');
  if (input.opportunities.length === 0) throw new Error('Nothing selected');

  const title = titleFor(input.clientName, input.opportunities.length);
  const brief = buildIdeationBrief(input);
  const queueNote = formatIdeationQueueNote(brief, await loadPromptTemplate('seo-diagnosis'));

  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: input.clientId,
      title,
      description: queueNote,
      status: 'In Progress',
      due_date: noonTodayIso(),
      assigned_by: input.createdBy.id,
      assigned_to: input.createdBy.id,
      assigned_to_name: input.createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  const job = await enqueueAiJob({
    task_id: task.id,
    title,
    card_id: null,
    created_by: input.createdBy.id,
    queue_note: queueNote,
    kind: 'ideation',
    brief,
    model: input.model ?? null,
  });

  // An RLS-blocked update returns no error and zero rows, so the stamp is read back rather than
  // assumed. The job is already real either way — this reports the mismatch instead of pretending
  // the click did nothing, which is the exact failure this rewrite exists to remove.
  const { data: stamped, error: stampError } = await supabase
    .from('seo_opportunities')
    .update({ status: 'diagnosing', job_id: job.id })
    .in('id', brief.opportunity_ids)
    .select('id');
  if (stampError) throw new Error(`Job ${job.id} is queued, but the rows still read as open: ${stampError.message}`);
  if ((stamped?.length ?? 0) !== brief.opportunity_ids.length) {
    throw new Error(
      `Job ${job.id} is queued, but only ${stamped?.length ?? 0} of ${brief.opportunity_ids.length} rows were marked as being read.`
    );
  }

  return job;
}

/**
 * One job per client in the selection. Selecting across clients is allowed on the all-clients
 * list, and silently mixing two sites into one diagnosis would produce a spec nobody can build.
 */
export async function enqueueIdeationJobs(
  opportunities: SeoOpportunity[],
  createdBy: { id: string; name: string },
  model?: string | null
): Promise<AiJob[]> {
  const byClient = new Map<string, SeoOpportunity[]>();
  for (const o of opportunities) {
    const list = byClient.get(o.client_id);
    if (list) list.push(o);
    else byClient.set(o.client_id, [o]);
  }

  const jobs: AiJob[] = [];
  for (const [clientId, rows] of byClient) {
    jobs.push(
      await enqueueIdeationJob({
        clientId,
        clientName: rows[0].client?.name ?? 'Unknown client',
        opportunities: rows,
        createdBy,
        model: model ?? null,
      })
    );
  }
  return jobs;
}
